import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { blogsService } from '../../services/blogs';
import { uploadService } from '../../services/upload';
import type { BlogPost } from '../../services/blogs';
import Navbar from '../../components/feature/Navbar';
import LoadingSpinner from '../../components/feature/LoadingSpinner';

export default function Profile() {
  const navigate = useNavigate();
  const { user, isAuthenticated, updateProfile } = useAuth();
  const [myBlogPosts, setMyBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'published' | 'drafts'>('published');
  
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ fullName: '', avatarUrl: '' });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const handlePublish = async (blogId: string) => {
    if (!window.confirm('¿Quieres publicar este artículo?')) {
      return;
    }

    try {
      await blogsService.publishBlogPost(blogId);
      await loadMyBlogPosts();
      alert('Artículo publicado correctamente');
    } catch (error) {
      console.error('Error publishing blog post:', error);
      alert('Error al publicar el artículo');
    }
  };

  useEffect(() => {
    if (user) {
      setEditForm({ fullName: user.fullName, avatarUrl: user.avatarUrl || '' });
    }
  }, [user]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      alert('Solo se permiten archivos de imagen');
      return;
    }

    // Validar tamaño (2MB para avatar)
    if (file.size > 2 * 1024 * 1024) {
      alert('El avatar no debe superar los 2MB');
      return;
    }

    setUploadingAvatar(true);

    try {
      const response = await uploadService.uploadImage(file);
      if (response.success) {
        setEditForm(prev => ({ 
          ...prev, 
          avatarUrl: response.data.url 
        }));
      } else {
        alert(response.message || 'Error al subir el avatar');
      }
    } catch (err: any) {
      alert(err.message || 'Error al subir el avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      await updateProfile({ fullName: editForm.fullName, avatarUrl: editForm.avatarUrl });
      setIsEditing(false);
      alert('Perfil actualizado correctamente');
    } catch {
      alert('Error al actualizar el perfil');
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    loadMyBlogPosts();
  }, [isAuthenticated, navigate]);

  const loadMyBlogPosts = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const posts = await blogsService.getBlogPostsByUser(user.id);
      setMyBlogPosts(posts);
    } catch (error) {
      console.error('Error loading blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blogId: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este artículo? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await blogsService.deleteBlogPost(blogId);
      await loadMyBlogPosts();
    } catch (error) {
      console.error('Error deleting blog post:', error);
      alert('Error al eliminar el artículo');
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const publishedPosts = myBlogPosts.filter(post => post.is_published);
  const draftPosts = myBlogPosts.filter(post => !post.is_published);

  const currentPosts = activeTab === 'published' ? publishedPosts : draftPosts;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-lp-bg">
        <Navbar />
        <div className="pt-20">
          <LoadingSpinner message="Verificando acceso..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lp-bg">
      <Navbar />
      
      <div className="pt-20 px-4 pb-8">
        {/* Header */}
        <div className="mb-6">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sepia-600 hover:text-sepia-800 font-ui text-sm mb-4 transition-colors duration-200"
          >
            <i className="ri-arrow-left-line"></i>
            Volver al inicio
          </button>
          
          <div className="vintage-paper aged-paper p-6 shadow-lg">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-16 h-16 bg-sepia-200/60 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <i className="ri-user-line text-2xl text-sepia-700"></i>
                )}
              </div>
              <div className="flex-1 w-full">
                {isEditing ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-ui text-sepia-600 block mb-1">Nombre</label>
                      <input 
                        type="text" 
                        value={editForm.fullName}
                        onChange={e => setEditForm({...editForm, fullName: e.target.value})}
                        className="w-full p-2 text-sm border border-sepia-300 rounded bg-sepia-50 focus:outline-none focus:ring-1 focus:ring-sepia-400"
                        placeholder="Nombre completo"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-ui text-sepia-600 block mb-1">Avatar</label>
                      <div className="flex flex-col gap-2">
                        {/* Preview */}
                        {editForm.avatarUrl && (
                          <div className="flex items-center gap-2 p-2 bg-sepia-50 rounded-[10%]">
                            <img 
                              src={editForm.avatarUrl} 
                              alt="Vista previa del avatar" 
                              className="w-12 h-12 rounded-full object-cover border-2 border-sepia-300"
                            />
                            <div className="text-xs text-sepia-600 font-ui">
                              Vista previa actual
                            </div>
                          </div>
                        )}
                        
                        <input 
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          disabled={uploadingAvatar}
                          className="w-full text-xs text-sepia-700
                            file:mr-4 file:py-1.5 file:px-3
                            file:rounded-[10%] file:border-0
                            file:text-xs file:font-ui file:bg-sepia-200 file:text-sepia-800
                            hover:file:bg-sepia-300 transition-colors
                            disabled:file:bg-gray-100 disabled:file:text-gray-500"
                        />
                        
                        {uploadingAvatar && (
                          <div className="flex items-center gap-2 text-xs text-sepia-600 font-ui">
                            <i className="ri-loader-4-line animate-spin"></i>
                            Subiendo avatar...
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                        <button 
                          onClick={handleUpdateProfile} 
                          className="text-xs bg-sepia-800 text-sepia-100 px-3 py-1.5 rounded hover:bg-sepia-900 transition-colors"
                        >
                          Guardar
                        </button>
                        <button 
                          onClick={() => setIsEditing(false)} 
                          className="text-xs bg-sepia-200 text-sepia-800 px-3 py-1.5 rounded hover:bg-sepia-300 transition-colors"
                        >
                          Cancelar
                        </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-start">
                      <h1 className="font-headings text-xl text-sepia-900">{user?.fullName}</h1>
                      <button 
                        onClick={() => setIsEditing(true)} 
                        className="text-sepia-600 hover:text-sepia-800 p-1 rounded hover:bg-sepia-100 transition-colors"
                        title="Editar perfil"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                    </div>
                    <p className="text-sm text-sepia-600 font-ui">{user?.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {user?.isAffiliated ? (
                        <span className="text-xs text-green-700 font-ui bg-green-100/50 px-3 py-1 rounded-full">
                          Usuario Afiliado
                        </span>
                      ) : (
                        <span className="text-xs text-sepia-600 font-ui bg-sepia-100/50 px-3 py-1 rounded-full">
                          Miembro
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3 pt-4 border-t border-sepia-300/50">
              <button
                onClick={() => navigate('/create-blog')}
                className="vintage-paper-subtle text-sepia-900 hover:bg-sepia-200/70 px-4 py-3 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md flex items-center justify-center gap-2"
              >
                <i className="ri-add-line"></i>
                Crear artículo
              </button>
              <button
                onClick={() => navigate('/search')}
                className="vintage-paper-subtle text-sepia-900 hover:bg-sepia-200/70 px-4 py-3 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md flex items-center justify-center gap-2"
              >
                <i className="ri-search-line"></i>
                Buscar
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="vintage-paper aged-paper p-6 shadow-lg mb-6">
          <h2 className="font-headings text-lg text-sepia-900 mb-4">TUS ESTADÍSTICAS</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-headings text-sepia-800">{publishedPosts.length}</div>
              <div className="text-xs text-sepia-600 font-ui">Publicados</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-headings text-sepia-800">{draftPosts.length}</div>
              <div className="text-xs text-sepia-600 font-ui">En revisión</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-headings text-sepia-800">{myBlogPosts.length}</div>
              <div className="text-xs text-sepia-600 font-ui">Total</div>
            </div>
          </div>
        </div>

        {/* My Articles */}
        <div className="vintage-paper aged-paper p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-headings text-lg text-sepia-900">MIS ARTÍCULOS</h2>
            <button
              onClick={loadMyBlogPosts}
              className="text-xs text-sepia-600 hover:text-sepia-800 font-ui transition-colors"
            >
              <i className="ri-refresh-line mr-1"></i>
              Actualizar
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setActiveTab('published')}
              className={`px-4 py-2 text-sm font-ui rounded-[15%] transition-all duration-200 ${
                activeTab === 'published'
                  ? 'vintage-paper-subtle text-sepia-900 shadow-md'
                  : 'text-sepia-600 hover:text-sepia-800'
              }`}
            >
              Publicados ({publishedPosts.length})
            </button>
            <button
              onClick={() => setActiveTab('drafts')}
              className={`px-4 py-2 text-sm font-ui rounded-[15%] transition-all duration-200 ${
                activeTab === 'drafts'
                  ? 'vintage-paper-subtle text-sepia-900 shadow-md'
                  : 'text-sepia-600 hover:text-sepia-800'
              }`}
            >
              En revisión ({draftPosts.length})
            </button>
          </div>

          {/* Articles List */}
          {loading ? (
            <div className="text-center py-6">
              <i className="ri-loader-4-line animate-spin text-xl text-sepia-400 mb-2"></i>
              <p className="text-sm text-sepia-500 font-ui">Cargando artículos...</p>
            </div>
          ) : currentPosts.length > 0 ? (
            <div className="space-y-4">
              {currentPosts.map((post) => (
                <div key={post.id} className="vintage-paper-subtle p-4 rounded-[10%] border border-sepia-300/30">
                  <div className="flex flex-wrap gap-2 mb-2">
                    <span className="text-xs font-ui text-sepia-600 bg-sepia-100/50 px-2 py-1 rounded-[10%]">
                      {post.category}
                    </span>
                    <span className="text-xs text-sepia-500 font-ui">
                      {formatDate(post.created_at)}
                    </span>
                    {!post.is_published && (
                      <span className="text-xs text-amber-700 font-ui bg-amber-100/50 px-2 py-1 rounded-[10%]">
                        En revisión
                      </span>
                    )}
                  </div>
                  
                  <h3 className="font-headings text-base text-sepia-900 mb-2 leading-snug">
                    {post.title}
                  </h3>
                  
                  <p className="text-sm text-sepia-700 font-body mb-3 leading-relaxed">
                    {post.excerpt}
                  </p>
                  
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {post.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="text-xs text-sepia-500 font-ui bg-sepia-50/50 px-2 py-0.5 rounded-[10%]">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-3 border-t border-sepia-300/30">
                    <div className="flex gap-2">
                      {post.is_published ? (
                        <button
                          onClick={() => navigate(`/blog/${post.id}`)}
                          className="text-xs text-sepia-600 hover:text-sepia-800 font-ui transition-colors flex items-center gap-1"
                        >
                          <i className="ri-eye-line"></i>
                          Ver
                        </button>
                      ) : (
                        <button
                          onClick={() => handlePublish(post.id!)}
                          className="text-xs text-green-700 hover:text-green-900 font-ui transition-colors flex items-center gap-1"
                        >
                          <i className="ri-upload-cloud-line"></i>
                          Publicar
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/edit-blog/${post.id}`)}
                        className="text-xs text-sepia-600 hover:text-sepia-800 font-ui transition-colors flex items-center gap-1"
                      >
                        <i className="ri-edit-line"></i>
                        Editar
                      </button>
                    </div>
                    <button 
                      onClick={() => handleDelete(post.id!)}
                      className="text-xs text-red-600 hover:text-red-800 font-ui transition-colors flex items-center gap-1"
                    >
                      <i className="ri-delete-bin-line"></i>
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <i className="ri-file-text-line text-3xl text-sepia-400 mb-3"></i>
              <h3 className="font-headings text-lg text-sepia-900 mb-2">
                {activeTab === 'published' ? 'No tienes artículos publicados' : 'No tienes artículos en revisión'}
              </h3>
              <p className="text-sm text-sepia-700 font-body mb-4">
                {activeTab === 'published' 
                  ? 'Crea tu primer artículo para compartir con la comunidad'
                  : 'Todos tus artículos han sido publicados'
                }
              </p>
              {activeTab === 'published' && (
                <button
                  onClick={() => navigate('/create-blog')}
                  className="vintage-paper-subtle text-sepia-900 hover:bg-sepia-200/70 px-4 py-2 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md"
                >
                  Crear primer artículo
                </button>
              )}
            </div>
          )}
        </div>

        {/* Vintage Background */}
        <div 
          className="fixed inset-0 pointer-events-none opacity-5 z-0"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=vintage%20personal%20study%20room%2C%20antique%20wooden%20desk%20with%20leather%20chair%2C%20old%20books%20and%20manuscripts%20organized%20on%20shelves%2C%20warm%20sepia%20lighting%2C%20personal%20library%20atmosphere%2C%20brass%20reading%20lamp%2C%20fountain%20pen%20and%20inkwell%2C%20aged%20paper%20documents%2C%20scholarly%20workspace%2C%20nostalgic%20academic%20setting%2C%20brown%20and%20cream%20tones&width=375&height=812&seq=profile_bg&orientation=portrait')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'multiply'
          }}
        />
      </div>
    </div>
  );
}
